import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  allFieldGroupsApproved,
  FIELD_GROUP_LABELS,
  PUBLISH_TARGETS,
  PUBLISH_TARGET_LABELS,
  type FieldGroupKey,
  type ListingPrizeDetails,
  type ListingTemplatePrize,
  type PublishTargetKey,
} from '@/api/listings'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, EmptyState, Icon, LoadingBlock } from '@/design'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import {
  useCreateListing,
  useFilterOptions,
  useGenerateListingContent,
  useListing,
  useListingTemplates,
  usePublishListing,
  useSetListingApproval,
  useUpdateListing,
} from './hooks'
import { ListingCopyPreview } from './ListingCopyPreview'
import { ListingStatusBadge } from './ListingStatusBadge'
import './listings.css'

interface FormState {
  prizeName: string
  prizeCategory: string
  prizeValue: string
  ticketPrice: string
  maxEntries: string
  instantWinsCount: string
  drawDate: string
  mainImageUrl: string
  additionalMediaUrls: string
  keySellingPoints: string
  prizeSpecifications: string
  competitionId: string
}

function defaultDrawDate(): string {
  const date = new Date()
  date.setDate(date.getDate() + 14)
  return date.toISOString().slice(0, 10)
}

function blankForm(): FormState {
  return {
    prizeName: '',
    prizeCategory: '',
    prizeValue: '',
    ticketPrice: '',
    maxEntries: '',
    instantWinsCount: '0',
    drawDate: defaultDrawDate(),
    mainImageUrl: '',
    additionalMediaUrls: '',
    keySellingPoints: '',
    prizeSpecifications: '',
    competitionId: '',
  }
}

/** The prize-field-to-form-string mapping every entry point (an existing listing, a template) goes through. */
function fromPrizeFields(prize: ListingTemplatePrize, drawDate: string, competitionId: string): FormState {
  return {
    prizeName: prize.prizeName,
    prizeCategory: prize.prizeCategory,
    prizeValue: String(prize.prizeValue),
    ticketPrice: String(prize.ticketPrice),
    maxEntries: String(prize.maxEntries),
    instantWinsCount: String(prize.instantWinsCount),
    drawDate,
    mainImageUrl: prize.mainImageUrl,
    additionalMediaUrls: prize.additionalMediaUrls.join('\n'),
    keySellingPoints: prize.keySellingPoints.join('\n'),
    prizeSpecifications: prize.prizeSpecifications,
    competitionId,
  }
}

function fromPrize(prize: ListingPrizeDetails, competitionId: string): FormState {
  return fromPrizeFields(prize, prize.drawDate, competitionId)
}

function fromTemplate(prize: ListingTemplatePrize): FormState {
  return fromPrizeFields(prize, defaultDrawDate(), '')
}

function lines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function toPrize(form: FormState): ListingPrizeDetails {
  return {
    prizeName: form.prizeName.trim(),
    prizeCategory: form.prizeCategory.trim(),
    prizeValue: Number(form.prizeValue) || 0,
    ticketPrice: Number(form.ticketPrice) || 0,
    maxEntries: Math.round(Number(form.maxEntries)) || 0,
    instantWinsCount: Math.round(Number(form.instantWinsCount)) || 0,
    drawDate: form.drawDate,
    mainImageUrl: form.mainImageUrl.trim(),
    additionalMediaUrls: lines(form.additionalMediaUrls),
    keySellingPoints: lines(form.keySellingPoints),
    prizeSpecifications: form.prizeSpecifications.trim(),
  }
}

/**
 * Create or edit a listing: prize and media details, then — once saved —
 * AI-drafted copy from the Generator port, a per-field-group approval, and
 * the publishing destinations Publish unlocks once both groups are approved
 * (pivot doc §6 Stage 3).
 */
export function ListingFormPage() {
  const { id } = useParams<{ id?: string }>()
  const notLive = useNotLivePage(id ? 'updateListing' : 'createListing')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const search = useFilterSearch()

  const listingQuery = useListing(id)
  const templatesQuery = useListingTemplates()
  const filterOptions = useFilterOptions()
  const createListing = useCreateListing()
  const updateListing = useUpdateListing()
  const generateContent = useGenerateListingContent()
  const setApproval = useSetListingApproval()
  const publish = usePublishListing()

  const listing = listingQuery.data?.data ?? null
  // A plain "new listing" shows a blank form immediately; "new from template" waits
  // one tick for the template lookup below, so it is never overwritten once set.
  const [form, setForm] = useState<FormState | null>(() =>
    id || searchParams.get('template') ? null : blankForm(),
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id && listing && !form) setForm(fromPrize(listing.prize, listing.competitionEntity?.id ?? ''))
  }, [id, listing, form])

  useEffect(() => {
    if (id || form || !templatesQuery.data) return
    const templateId = searchParams.get('template')
    const template = templatesQuery.data.data.find((candidate) => candidate.id === templateId)
    setForm(template ? fromTemplate(template.prize) : blankForm())
    // Only ever prefills once, the first time templates arrive for a blank new-listing form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesQuery.data, id])

  // No form, and so no save, generate, approve or publish: nothing drafted here would be kept.
  if (notLive) return <NotLivePage />

  if (id && listingQuery.isPending) return <LoadingBlock rows={8} />
  if (id && listingQuery.isError) return <EmptyState variant="outage" description="This listing could not be loaded." />
  if (id && !listing) return <EmptyState variant="no-results" title="Listing not found" description="It may have been removed." />
  if (!form) return <LoadingBlock rows={8} />

  const currentForm = form

  function field<K extends keyof FormState>(key: K) {
    return {
      value: currentForm[key],
      onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setForm((current) => (current ? { ...current, [key]: event.target.value } : current)),
    }
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault()
    setError(null)
    const input = { prize: toPrize(currentForm), competitionId: currentForm.competitionId || null }
    try {
      if (listing) {
        await updateListing.mutateAsync([listing.id, input])
      } else {
        const created = await createListing.mutateAsync([input])
        navigate({ pathname: `/competitions/listings/${created.data.id}`, search }, { replace: true })
      }
    } catch {
      setError('Could not save this listing. Check the fields and try again.')
    }
  }

  async function handleGenerate() {
    if (!listing) return
    setError(null)
    try {
      await generateContent.mutateAsync([listing.id])
    } catch {
      setError('Could not generate content for this listing.')
    }
  }

  async function handleApprove(group: FieldGroupKey, approved: boolean) {
    if (!listing) return
    await setApproval.mutateAsync([listing.id, group, approved])
  }

  async function handlePublish(target: PublishTargetKey) {
    if (!listing) return
    setError(null)
    try {
      await publish.mutateAsync([listing.id, target])
    } catch {
      setError('Approve every field group before publishing.')
    }
  }

  const competitionOptions = filterOptions.data?.competition ?? []
  const canPublish = listing ? allFieldGroupsApproved(listing.approvals) : false

  return (
    <div className="lst-form-page">
      <Card
        title={listing ? (listing.copy?.title ?? listing.prize.prizeName) : 'New listing'}
        subtitle={listing ? <ListingStatusBadge status={listing.status} /> : 'Prize and media details'}
      >
        <form className="lst-form" onSubmit={(event) => void handleSave(event)}>
          <label>
            Prize name
            <input {...field('prizeName')} required />
          </label>
          <label>
            Prize category
            <input {...field('prizeCategory')} required />
          </label>
          <label>
            Prize value
            <input type="number" min="0" step="0.01" {...field('prizeValue')} required />
          </label>
          <label>
            Ticket price
            <input type="number" min="0" step="0.01" {...field('ticketPrice')} required />
          </label>
          <label>
            Max entries
            <input type="number" min="1" step="1" {...field('maxEntries')} required />
          </label>
          <label>
            Instant wins
            <input type="number" min="0" step="1" {...field('instantWinsCount')} />
          </label>
          <label>
            Draw / end date
            <input type="date" {...field('drawDate')} required />
          </label>
          <label>
            Linked competition (for pacing)
            <select {...field('competitionId')}>
              <option value="">No linked competition</option>
              {competitionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Main image URL
            <input {...field('mainImageUrl')} placeholder="https://…" />
          </label>
          <label className="lst-form__wide">
            Additional media (one URL per line)
            <textarea rows={2} {...field('additionalMediaUrls')} />
          </label>
          <label className="lst-form__wide">
            Key selling points (one per line)
            <textarea rows={3} {...field('keySellingPoints')} />
          </label>
          <label className="lst-form__wide">
            Prize specifications
            <textarea rows={3} {...field('prizeSpecifications')} />
          </label>
          {error ? (
            <p role="alert" className="lst-form__error">
              {error}
            </p>
          ) : null}
          <div className="lst-form__actions">
            <button
              className="ci-button"
              data-variant="primary"
              type="submit"
              disabled={createListing.isPending || updateListing.isPending}
            >
              {listing ? 'Save changes' : 'Create listing'}
            </button>
          </div>
        </form>
      </Card>

      {listing ? (
        <>
          <Card
            title={FIELD_GROUP_LABELS.prizeDetails}
            actions={
              <label className="lst-approve">
                <input
                  type="checkbox"
                  checked={listing.approvals.prizeDetails.approved}
                  onChange={(event) => void handleApprove('prizeDetails', event.target.checked)}
                />
                Approved
              </label>
            }
          >
            <p className="lst-muted">Review the prize and media fields above, then approve them for publishing.</p>
          </Card>

          <Card
            title="AI-drafted content"
            subtitle="Generated from the prize details, behind the Generator port. Review before approving."
            actions={
              <div className="lst-copy-actions">
                <button
                  className="ci-button"
                  type="button"
                  onClick={() => void handleGenerate()}
                  disabled={generateContent.isPending}
                >
                  <Icon name="sparkle" size={14} />
                  {listing.copy ? 'Regenerate' : 'Generate listing content'}
                </button>
                {listing.copy ? (
                  <label className="lst-approve">
                    <input
                      type="checkbox"
                      checked={listing.approvals.copy.approved}
                      onChange={(event) => void handleApprove('copy', event.target.checked)}
                    />
                    Approved
                  </label>
                ) : null}
              </div>
            }
          >
            {listing.copy ? (
              <ListingCopyPreview copy={listing.copy} />
            ) : (
              <EmptyState
                variant="no-data"
                compact
                title="No draft yet"
                description="Generate listing content to get a reviewable draft from the mock generator."
              />
            )}
          </Card>

          <Card
            title="Publishing destinations"
            subtitle={canPublish ? 'Choose where to publish this listing.' : 'Approve both field groups above to enable publishing.'}
          >
            <ul className="lst-targets">
              {PUBLISH_TARGETS.map((target) => {
                const state = listing.publishing.find((candidate) => candidate.target === target)
                const published = state?.status === 'published'
                // A published target goes stale when an approval is reset after it went live;
                // what is live then predates an edit or regeneration and must be republished.
                const stale = published && state?.stale === true
                return (
                  <li key={target} className="lst-target">
                    <span>{PUBLISH_TARGET_LABELS[target]}</span>
                    {published && !stale ? (
                      <span className="lst-target__done">Published</span>
                    ) : (
                      <button
                        className="ci-button"
                        type="button"
                        disabled={!canPublish || publish.isPending}
                        onClick={() => void handlePublish(target)}
                        aria-label={published ? `Republish to ${PUBLISH_TARGET_LABELS[target]}` : undefined}
                      >
                        {published ? 'Republish' : 'Publish'}
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
            {listing.publishLog.length > 0 ? (
              <ul className="lst-log">
                {listing.publishLog.map((entry) => (
                  <li key={entry.id}>
                    <strong>{PUBLISH_TARGET_LABELS[entry.target]}</strong> — {entry.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        </>
      ) : null}
    </div>
  )
}
